// ============================================================================
// RICHTEXTEDITOR.JS - EDITOR WYSIWYG COMPLETO
// VERSION FINAL: Todas las funcionalidades + Tablas con menú contextual + TAB
// ============================================================================

import React, { useState, useRef, useEffect } from 'react';
import { sanitizeHtml, htmlToText } from '../utils/sanitizeHtml';
import {
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  Link,
  Image,
  Quote,
  Code,
  Undo,
  Redo,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Eye,
  Edit3,
  Loader2,
  Settings,
  X,
  Eraser,
  Minus,
  FileCode,
  Strikethrough,
  Type,
  Highlighter,
  Table,
  Trash2,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  MoreVertical
} from 'lucide-react';
import '../styles/rich-text-editor.css';
import '../styles/mention-autocomplete.css';
import { useAuth } from '../auth/AuthContext';
import { highlightCodeBlocks } from '../utils/codeHighlight';
import MentionService from '../services/MentionService';
import useToast from '../services/useToast';
import apiClient from '../services/apiClient';


// En compacto (hasta 767 px) la cabecera de la app es sticky: lo que se fija
// con position: fixed tiene que quedar por debajo de ella, no taparla. En
// escritorio devuelve 0 y el comportamiento es el de siempre.
const getAppHeaderOffset = () => {
  if (typeof window === 'undefined' || !window.matchMedia || !window.matchMedia('(max-width: 767px)').matches) return 0;
  const header = document.querySelector('.header');
  if (!header) return 0;
  const pos = window.getComputedStyle(header).position;
  if (pos !== 'sticky' && pos !== 'fixed') return 0;
  return Math.max(0, Math.round(header.getBoundingClientRect().bottom));
};

function RichTextEditor({
  value = '',
  onChange,
  placeholder = 'Escribe aquí...',
  height = '300px',
  readonly = false,
  showToolbar = true,
  className = '',
  taskId = null,
  // Opt-in: el autocompletado @usuario solo se activa en el editor de
  // comentarios (TaskCommentTimeline). Documentación y formularios quedan IGUAL.
  enableMentions = false
}) {
  const [content, setContent] = useState(value);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showResizeModal, setShowResizeModal] = useState(false);
  const [originalImageDimensions, setOriginalImageDimensions] = useState({});
  const [showHTMLMode, setShowHTMLMode] = useState(false);
  const [htmlCode, setHtmlCode] = useState('');
  const [showTableModal, setShowTableModal] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableMenuPosition, setTableMenuPosition] = useState(null);
  const [showTableMenu, setShowTableMenu] = useState(false);

  // --- Autocompletado de menciones @usuario (solo si enableMentions) ---
  const [mentionUsers, setMentionUsers] = useState([]);        // lista cacheada del tenant
  const [mentionLoading, setMentionLoading] = useState(false);
  const [mentionOpen, setMentionOpen] = useState(false);       // dropdown visible
  const [mentionQuery, setMentionQuery] = useState('');        // texto tras la @ ("ju...")
  const [mentionIndex, setMentionIndex] = useState(0);         // opción resaltada (teclado)
  const [mentionPos, setMentionPos] = useState({ top: 0, left: 0 }); // coords viewport del caret
  // Rango (offsets) del "@query" en su nodo de texto, para sustituirlo al elegir.
  const mentionRangeRef = useRef(null);
  // True mientras el ratón interactúa con el dropdown (evita cierre por blur).
  const mentionInteractingRef = useRef(false);
  // Último query detectado, para no resetear el índice cuando no cambia.
  const mentionQueryRef = useRef('');

  const editorRef = useRef(null);
  // Manejadores del render actual para los listeners nativos (paste), que se
  // registran en un efecto y si no capturarían el onChange de aquel render.
  const editorHandlersRef = useRef({});
  const fileInputRef = useRef(null);
  const previewRef = useRef(null);
  const toolbarRef = useRef(null);
  const editorContainerRef = useRef(null);
  const [toolbarStuck, setToolbarStuck] = useState(false);
  const [toolbarDimensions, setToolbarDimensions] = useState({ width: 0, left: 0, top: 0 });
  // Móvil: la barra de formato arranca colapsada (solo se ven los botones de
  // modo + el toggle); el toggle "Formato" la despliega. En desktop se ignora
  // (la barra siempre se ve, via CSS).
  const [toolbarOpen, setToolbarOpen] = useState(false);
  const { token } = useAuth();
  const { confirm, showError, showWarning } = useToast();

  const imageSizes = [
    { name: 'Muy pequeña', width: 150, label: '150px' },
    { name: 'Pequeña', width: 250, label: '250px' },
    { name: 'Mediana', width: 400, label: '400px' },
    { name: 'Grande', width: 600, label: '600px' },
    { name: 'Muy grande', width: 800, label: '800px' },
    { name: 'Original', width: 'original', label: 'Tamaño natural' }
  ];

  useEffect(() => {
    if (value !== content) {
      setContent(value);
    }
  }, [value]);

  useEffect(() => {
    if (editorRef.current && !showHTMLMode && !isPreviewMode) {
      // No reasignar innerHTML si el editor tiene foco (rompería el caret
      // y provocaría scroll al top al escribir, p. ej. dentro de una tabla).
      if (document.activeElement === editorRef.current) return;
      if (editorRef.current.innerHTML !== content) {
        editorRef.current.innerHTML = sanitizeHtml(content);
      }
    }
  }, [content, showHTMLMode, isPreviewMode]);

  // Aplicar syntax highlighting cuando se entra en modo preview
  useEffect(() => {
    if (isPreviewMode && previewRef.current) {
      highlightCodeBlocks(previewRef.current).catch(() => {});
    }
  }, [isPreviewMode, content]);

  // Toolbar sticky mediante scroll listener (los overflow: hidden en ancestros rompen position: sticky)
  useEffect(() => {
    if (readonly || !showToolbar) return;

    const handleScroll = () => {
      const container = editorContainerRef.current;
      const toolbar = toolbarRef.current;
      if (!container || !toolbar) return;

      const containerRect = container.getBoundingClientRect();
      const toolbarHeight = toolbar.offsetHeight;
      let offsetTop = getAppHeaderOffset();
      // En compacto, dentro de la descripción de una tarea su cabecera
      // (Descripción, Guardar, Cancelar) también se fija bajo la de la app:
      // la barra va debajo de las dos. Se calcula con la altura de esa
      // cabecera, sin esperar a que se pinte fija, para no ir un scroll tarde.
      if (offsetTop > 0) {
        const descHeader = container.closest('.task-description-section')?.querySelector('.description-header');
        if (descHeader) offsetTop += descHeader.offsetHeight;
      }

      // Si el top del contenedor ha pasado la vista (o la cabecera sticky en
      // compacto) y el bottom aún no
      const shouldStick = containerRect.top < offsetTop && containerRect.bottom > toolbarHeight + 100 + offsetTop;

      if (shouldStick !== toolbarStuck) {
        setToolbarStuck(shouldStick);
      }

      if (shouldStick) {
        setToolbarDimensions({
          width: containerRect.width,
          left: containerRect.left,
          top: offsetTop,
        });
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [readonly, showToolbar, toolbarStuck]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || readonly) return;

    const handlePaste = async (event) => {
      const clipboardData = event.clipboardData || event.originalEvent.clipboardData;
      const items = clipboardData.items;

      // 1. Verificar si hay imágenes (prioridad)
      for (const item of items) {
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          event.preventDefault();
          const file = item.getAsFile();
          if (!file) continue;

          if (taskId) {
            await editorHandlersRef.current.handleImageUploadToServer(file);
          } else {
            await editorHandlersRef.current.handleImageUploadBase64(file);
          }
          return;
        }
      }

      // 2. Para texto HTML: limpiar formato agresivamente al pegar
      const html = clipboardData.getData('text/html');
      const plainText = clipboardData.getData('text/plain');
      if (html) {
        event.preventDefault();
        // DOMParser crea un documento inerte: no ejecuta onerror ni carga
        // imágenes del portapapeles mientras se limpia.
        const pasteDoc = new DOMParser().parseFromString(html, 'text/html');
        const temp = pasteDoc.body;

        // Eliminar tags no deseados completamente
        temp.querySelectorAll('meta, style, link, script, title, xml, svg, img[src^="data:"]').forEach(el => el.remove());

        // Tags permitidos - solo formato básico
        const allowedTags = new Set([
          'P', 'BR', 'DIV',
          'B', 'STRONG', 'I', 'EM', 'U',
          'A',
          'UL', 'OL', 'LI',
          'TABLE', 'THEAD', 'TBODY', 'TR', 'TH', 'TD',
          'BLOCKQUOTE',
          'CODE', 'PRE'
        ]);

        // Convertir headings a párrafos con negrita
        temp.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach(el => {
          const p = pasteDoc.createElement('p');
          const bold = pasteDoc.createElement('strong');
          bold.innerHTML = el.innerHTML;
          p.appendChild(bold);
          el.replaceWith(p);
        });

        // Convertir <font>, <span> y tags desconocidos a texto inline
        const unwrapTag = (el) => {
          const parent = el.parentNode;
          while (el.firstChild) {
            parent.insertBefore(el.firstChild, el);
          }
          parent.removeChild(el);
        };

        // Múltiples pasadas para tags anidados
        for (let i = 0; i < 3; i++) {
          temp.querySelectorAll('*').forEach(el => {
            if (!allowedTags.has(el.tagName)) {
              unwrapTag(el);
            }
          });
        }

        // Limpiar todos los atributos excepto href en links
        temp.querySelectorAll('*').forEach(el => {
          const attrs = [...el.attributes];
          attrs.forEach(attr => {
            if (el.tagName === 'A' && attr.name === 'href') return;
            el.removeAttribute(attr.name);
          });
        });

        // Limpiar whitespace excesivo y divs vacíos
        temp.querySelectorAll('div:empty, p:empty, span:empty').forEach(el => {
          if (!el.querySelector('br')) el.remove();
        });

        const cleanHTML = temp.innerHTML
          .replace(/\n{3,}/g, '\n\n')
          .trim();

        if (cleanHTML) {
          document.execCommand('insertHTML', false, sanitizeHtml(cleanHTML));
        } else if (plainText) {
          // Fallback a texto plano si la limpieza eliminó todo
          const escaped = plainText.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
          const paragraphs = escaped.split(/\n\n+/).map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('');
          document.execCommand('insertHTML', false, paragraphs);
        }
        editorHandlersRef.current.handleContentChange();
      }
    };

    editor.addEventListener('paste', handlePaste);
    return () => editor.removeEventListener('paste', handlePaste);
    // showHTMLMode/isPreviewMode: el div editable se vuelve a montar al salir
    // de esos modos y hay que enganchar el listener al nodo nuevo.
  }, [taskId, readonly, showHTMLMode, isPreviewMode]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || readonly || isPreviewMode) return;

    const handleClick = (event) => {
      if (event.target.tagName === 'IMG') {
        event.preventDefault();
        event.stopPropagation();
        selectImageForResize(event.target);
        setSelectedTable(null);
        setTableMenuPosition(null);
        setShowTableMenu(false);
        return;
      }
      
      if (event.target.closest('.rte-table-menu-button')) {
        event.preventDefault();
        event.stopPropagation();
        setShowTableMenu(!showTableMenu);
        return;
      }
      
      if (event.target.closest('.rte-table-menu')) {
        return;
      }
      
      const tableCell = event.target.closest('td, th');
      if (tableCell) {
        const table = tableCell.closest('table');
        if (table) {
          event.preventDefault();
          event.stopPropagation();
          selectTable(table, event);
          deselectImage();
          return;
        }
      }
      
      deselectImage();
      setSelectedTable(null);
      setTableMenuPosition(null);
      setShowTableMenu(false);
    };

    const handleImageDoubleClick = (event) => {
      if (event.target.tagName === 'IMG') {
        event.preventDefault();
        event.stopPropagation();
        selectImageForResize(event.target);
        setShowResizeModal(true);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        deselectImage();
        setSelectedTable(null);
        setTableMenuPosition(null);
        setShowTableMenu(false);
      }
    };

    editor.addEventListener('click', handleClick);
    editor.addEventListener('dblclick', handleImageDoubleClick);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      editor.removeEventListener('click', handleClick);
      editor.removeEventListener('dblclick', handleImageDoubleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [readonly, isPreviewMode, showTableMenu]);

  const handleKeyDown = (event) => {
    // El dropdown de menciones tiene prioridad sobre Tab/Enter del editor.
    if (handleMentionKeyDown(event)) {
      return;
    }

    if (event.key === 'Tab') {
      const tableCell = event.target.closest('td, th');
      if (tableCell) {
        event.preventDefault();
        
        const table = tableCell.closest('table');
        const cells = Array.from(table.querySelectorAll('td, th'));
        const currentIndex = cells.indexOf(tableCell);
        
        if (event.shiftKey) {
          if (currentIndex > 0) {
            cells[currentIndex - 1].focus();
            const range = document.createRange();
            range.selectNodeContents(cells[currentIndex - 1]);
            const selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(range);
          }
        } else {
          if (currentIndex < cells.length - 1) {
            cells[currentIndex + 1].focus();
            const range = document.createRange();
            range.selectNodeContents(cells[currentIndex + 1]);
            const selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(range);
          }
        }
        return;
      }
    }
    
    if (event.key === 'Enter' && !event.shiftKey) {
      const selection = window.getSelection();
      if (!selection.rangeCount) return;
      
      const range = selection.getRangeAt(0);
      let currentNode = range.startContainer;
      
      let blockElement = null;
      let node = currentNode;
      
      while (node && node !== editorRef.current) {
        if (node.nodeName === 'BLOCKQUOTE' || node.nodeName === 'PRE') {
          blockElement = node;
          break;
        }
        node = node.parentNode;
      }
      
      if (blockElement) {
        const textContent = blockElement.textContent;
        const cursorPosition = range.startOffset;
        const parentText = range.startContainer.textContent || '';
        
        const isAtEnd = cursorPosition >= parentText.length;
        const currentLineEmpty = parentText.trim() === '' || 
                                 (parentText.slice(cursorPosition).trim() === '');
        
        if (isAtEnd && currentLineEmpty) {
          event.preventDefault();
          
          const newParagraph = document.createElement('p');
          newParagraph.innerHTML = '<br>';
          
          if (textContent.trim() === '') {
            blockElement.parentNode.replaceChild(newParagraph, blockElement);
          } else {
            blockElement.parentNode.insertBefore(newParagraph, blockElement.nextSibling);
          }
          
          const newRange = document.createRange();
          newRange.setStart(newParagraph, 0);
          newRange.collapse(true);
          selection.removeAllRanges();
          selection.addRange(newRange);
          
          handleContentChange();
        }
      }
    }
  };

  const clearFormatting = () => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    
    document.execCommand('removeFormat', false, null);
    document.execCommand('unlink', false, null);
    
    const range = selection.getRangeAt(0);
    const container = range.commonAncestorContainer;
    
    const cleanNode = (node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        node.removeAttribute('style');
        node.removeAttribute('class');
        node.removeAttribute('id');
        
        const allowedTags = ['B', 'I', 'U', 'STRONG', 'EM', 'BR', 'P', 'DIV', 'SPAN'];
        
        if (!allowedTags.includes(node.tagName) && node !== editorRef.current) {
          const fragment = document.createDocumentFragment();
          while (node.firstChild) {
            fragment.appendChild(node.firstChild);
          }
          node.parentNode.replaceChild(fragment, node);
        }
        
        Array.from(node.childNodes).forEach(cleanNode);
      }
    };
    
    if (!selection.isCollapsed) {
      const fragment = range.cloneContents();
      Array.from(fragment.childNodes).forEach(cleanNode);
      
      const walker = document.createTreeWalker(
        container,
        NodeFilter.SHOW_ELEMENT,
        null,
        false
      );
      
      let node;
      while ((node = walker.nextNode())) {
        if (selection.containsNode(node, true)) {
          node.removeAttribute('style');
          node.removeAttribute('class');
          node.removeAttribute('id');
        }
      }
    }
    
    editorRef.current?.focus();
    handleContentChange();
  };

  const selectImageForResize = (img) => {
    const previousSelected = editorRef.current?.querySelector('.rte-image-selected');
    if (previousSelected) {
      previousSelected.classList.remove('rte-image-selected');
    }
    
    setSelectedImage(img);
    img.classList.add('rte-image-selected');
    
    const imgId = img.src + img.alt;
    if (!originalImageDimensions[imgId]) {
      if (img.complete) {
        setOriginalImageDimensions(prev => ({
          ...prev,
          [imgId]: { width: img.naturalWidth, height: img.naturalHeight }
        }));
      } else {
        img.onload = () => {
          setOriginalImageDimensions(prev => ({
            ...prev,
            [imgId]: { width: img.naturalWidth, height: img.naturalHeight }
          }));
        };
      }
    }
  };

  const deselectImage = () => {
    if (selectedImage) {
      selectedImage.classList.remove('rte-image-selected');
    }
    
    const editor = editorRef.current;
    if (editor) {
      const allSelectedImages = editor.querySelectorAll('.rte-image-selected');
      allSelectedImages.forEach(img => {
        img.classList.remove('rte-image-selected');
      });
    }
    
    setSelectedImage(null);
    setShowResizeModal(false);
  };

  const openResizeModal = () => {
    if (selectedImage) {
      setShowResizeModal(true);
    }
  };

  const applyImageSize = (sizeOption) => {
    if (!selectedImage) return;
    
    const imgId = selectedImage.src + selectedImage.alt;
    const originalDims = originalImageDimensions[imgId];
    
    if (sizeOption.width === 'original' && originalDims) {
      selectedImage.style.width = originalDims.width + 'px';
      selectedImage.style.height = originalDims.height + 'px';
    } else {
      const targetWidth = sizeOption.width;
      
      if (originalDims) {
        const aspectRatio = originalDims.width / originalDims.height;
        const targetHeight = Math.round(targetWidth / aspectRatio);
        
        selectedImage.style.width = targetWidth + 'px';
        selectedImage.style.height = targetHeight + 'px';
      } else {
        selectedImage.style.width = targetWidth + 'px';
        selectedImage.style.height = 'auto';
      }
    }
    
    handleContentChange();
    setShowResizeModal(false);
  };

  const handleContentChange = () => {
    if (editorRef.current) {
      const rawContent = editorRef.current.innerHTML;
      const newContent = sanitizeEditorHtml(rawContent);
      setContent(newContent);
      if (onChange) {
        onChange(newContent);
      }

      if (selectedImage && !editorRef.current.contains(selectedImage)) {
        setSelectedImage(null);
      }

      // Reevaluar el trigger @ tras cada cambio de contenido.
      if (enableMentions && !readonly) {
        detectMentionTrigger();
      }
    }
  };

  // Limpia clases de selección que no deben persistirse
  const sanitizeEditorHtml = (html) => {
    if (!html) return html;
    // <template>: su contenido es inerte (no ejecuta onerror al parsear).
    const temp = document.createElement('template');
    temp.innerHTML = html;
    const root = temp.content;
    root.querySelectorAll('.rte-table-selected').forEach(el => el.classList.remove('rte-table-selected'));
    root.querySelectorAll('.rte-image-selected').forEach(el => el.classList.remove('rte-image-selected'));
    // Menciones: en el editor el chip lleva contenteditable="false" para
    // comportarse como una unidad atómica, pero el HTML serializado debe ser
    // EXACTAMENTE el del contrato §3 (solo class="mention" + data-user-id).
    root.querySelectorAll('span.mention').forEach(el => {
      el.removeAttribute('contenteditable');
      const uid = el.getAttribute('data-user-id');
      // data-user-id SIEMPRE numérico; si no lo es, degradar a texto plano.
      if (!uid || !/^\d+$/.test(uid)) {
        el.replaceWith(document.createTextNode(el.textContent || ''));
      }
    });
    root.querySelectorAll('[class=""]').forEach(el => el.removeAttribute('class'));
    return temp.innerHTML;
  };

  // ==========================================================================
  // AUTOCOMPLETADO DE MENCIONES @usuario (Fase 3) — solo si enableMentions
  // ==========================================================================

  // Cargar (y cachear) la lista de usuarios del tenant la primera vez que se
  // activa el editor de comentarios. MentionService cachea entre instancias.
  useEffect(() => {
    if (!enableMentions || readonly) return;
    let cancelled = false;
    setMentionLoading(true);
    MentionService.getMentionableUsers()
      .then((users) => {
        if (!cancelled) setMentionUsers(Array.isArray(users) ? users : []);
      })
      .finally(() => {
        if (!cancelled) setMentionLoading(false);
      });
    return () => { cancelled = true; };
  }, [enableMentions, readonly]);

  // Lista filtrada por el texto tras la @ (case/acentos-insensible).
  const normalizeStr = (s) =>
    (s || '')
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();

  const filteredMentionUsers = React.useMemo(() => {
    if (!mentionOpen) return [];
    const q = normalizeStr(mentionQuery);
    if (!q) return mentionUsers.slice(0, 8);
    return mentionUsers
      .filter((u) => normalizeStr(u.nombre).includes(q))
      .slice(0, 8);
  }, [mentionOpen, mentionQuery, mentionUsers]);

  const closeMentionDropdown = () => {
    setMentionOpen(false);
    setMentionQuery('');
    setMentionIndex(0);
    mentionRangeRef.current = null;
    mentionQueryRef.current = '';
    mentionInteractingRef.current = false;
  };

  // Mantener el índice resaltado dentro de los límites de la lista filtrada.
  useEffect(() => {
    if (!mentionOpen) return;
    const len = filteredMentionUsers.length;
    if (len === 0) return;
    if (mentionIndex > len - 1) setMentionIndex(len - 1);
  }, [filteredMentionUsers, mentionOpen, mentionIndex]);

  // Calcula la posición (viewport) del caret para anclar el dropdown.
  // Hace "flip" hacia arriba si no cabe abajo, y clampa al ancho de pantalla.
  const computeCaretPosition = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;
    const range = selection.getRangeAt(0).cloneRange();
    range.collapse(true);
    let rect = range.getClientRects()[0];
    if (!rect) {
      // Caret en un nodo vacío: medir con un marcador temporal.
      const marker = document.createElement('span');
      marker.textContent = '​';
      range.insertNode(marker);
      rect = marker.getBoundingClientRect();
      const parent = marker.parentNode;
      parent.removeChild(marker);
      parent.normalize();
    }
    if (!rect) return null;

    const DROPDOWN_H = 264; // coincide con max-height del CSS
    const DROPDOWN_W = 280;
    const margin = 8;

    // Flip arriba si abajo no cabe pero arriba sí.
    const spaceBelow = window.innerHeight - rect.bottom;
    let top;
    if (spaceBelow < DROPDOWN_H + margin && rect.top > DROPDOWN_H + margin) {
      top = rect.top - DROPDOWN_H - 4;
    } else {
      top = rect.bottom + 4;
    }

    // Clamp horizontal al viewport.
    let left = rect.left;
    if (left + DROPDOWN_W + margin > window.innerWidth) {
      left = Math.max(margin, window.innerWidth - DROPDOWN_W - margin);
    }

    return { top: Math.max(margin, top), left: Math.max(margin, left) };
  };

  // Tras escribir, decide si hay un "@token" activo justo antes del caret.
  const detectMentionTrigger = () => {
    if (!enableMentions || readonly) return;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !selection.isCollapsed) {
      if (mentionOpen) closeMentionDropdown();
      return;
    }
    const range = selection.getRangeAt(0);
    const node = range.startContainer;
    // Solo dentro de nodos de texto del propio editor.
    if (node.nodeType !== Node.TEXT_NODE || !editorRef.current?.contains(node)) {
      if (mentionOpen) closeMentionDropdown();
      return;
    }
    const textBefore = node.textContent.slice(0, range.startOffset);
    // @ precedido de inicio o de un espacio/no-palabra; query sin espacios ni @.
    const match = textBefore.match(/(^|[\s (])@([^\s@ ]*)$/);
    if (!match) {
      if (mentionOpen) closeMentionDropdown();
      return;
    }
    const query = match[2];
    // Guardar dónde empieza el "@" para sustituirlo luego.
    const atStart = range.startOffset - query.length - 1;
    mentionRangeRef.current = {
      node,
      atStart,
      end: range.startOffset
    };
    const pos = computeCaretPosition();
    if (pos) setMentionPos(pos);
    // Solo reiniciar la opción resaltada si el texto tecleado cambió: así
    // ↑/↓ (que no mueven el caret) no pierden la selección por teclado.
    if (mentionQueryRef.current !== query) {
      mentionQueryRef.current = query;
      setMentionQuery(query);
      setMentionIndex(0);
    }
    if (!mentionOpen) setMentionOpen(true);
  };

  // Inserta el span de la mención sustituyendo el "@query" tecleado.
  const insertMention = (user) => {
    const stored = mentionRangeRef.current;
    const editor = editorRef.current;
    if (!stored || !editor) {
      closeMentionDropdown();
      return;
    }
    const { node, atStart, end } = stored;
    try {
      // Seleccionar exactamente el "@query" y borrarlo.
      const delRange = document.createRange();
      delRange.setStart(node, Math.max(0, atStart));
      delRange.setEnd(node, end);
      delRange.deleteContents();

      // Construir el chip EXACTO del contrato §3 (+ contenteditable=false para
      // tratarlo como unidad en el editor; se quita al serializar).
      const span = document.createElement('span');
      span.className = 'mention';
      span.setAttribute('data-user-id', String(user.id));
      span.setAttribute('contenteditable', 'false');
      span.textContent = `@${user.nombre}`;

      const space = document.createTextNode(' '); // espacio detrás
      delRange.insertNode(space);
      delRange.insertNode(span);

      // Caret tras el espacio.
      const sel = window.getSelection();
      const after = document.createRange();
      after.setStartAfter(space);
      after.collapse(true);
      sel.removeAllRanges();
      sel.addRange(after);
    } catch (e) {
      console.error('Error insertando mención:', e);
    }

    closeMentionDropdown();
    editor.focus();
    handleContentChange();
  };

  // Teclas del dropdown de menciones. Devuelve true si consumió la tecla.
  const handleMentionKeyDown = (event) => {
    if (!mentionOpen) return false;
    const items = filteredMentionUsers;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (items.length) setMentionIndex((i) => (i + 1) % items.length);
        return true;
      case 'ArrowUp':
        event.preventDefault();
        if (items.length) setMentionIndex((i) => (i - 1 + items.length) % items.length);
        return true;
      case 'Enter':
      case 'Tab':
        if (items.length) {
          event.preventDefault();
          insertMention(items[Math.min(mentionIndex, items.length - 1)]);
          return true;
        }
        // Sin resultados: dejar pasar la tecla y cerrar.
        closeMentionDropdown();
        return false;
      case 'Escape':
        event.preventDefault();
        event.stopPropagation();
        closeMentionDropdown();
        return true;
      default:
        return false;
    }
  };

  // Al perder el foco el editor, cerrar el dropdown (con margen para que el
  // mousedown sobre una opción —que hace preventDefault— se procese antes).
  const handleEditorBlur = () => {
    if (!mentionOpen) return;
    setTimeout(() => {
      if (!mentionInteractingRef.current) {
        closeMentionDropdown();
      }
    }, 120);
  };

  // Iniciales para el avatar de la opción.
  const mentionInitials = (name) => {
    if (!name) return '?';
    return name.trim().split(/\s+/).map((n) => n[0]).join('').substring(0, 2).toUpperCase();
  };

  // Resalta el trozo del nombre que coincide con lo tecleado.
  const renderHighlightedName = (name) => {
    const q = mentionQuery;
    if (!q) return name;
    const idx = normalizeStr(name).indexOf(normalizeStr(q));
    if (idx === -1) return name;
    return (
      <>
        {name.slice(0, idx)}
        <mark>{name.slice(idx, idx + q.length)}</mark>
        {name.slice(idx + q.length)}
      </>
    );
  };

  const MentionAutocomplete = () => {
    if (!enableMentions || !mentionOpen) return null;
    const items = filteredMentionUsers;

    return (
      <div
        className="mention-autocomplete"
        id="mention-autocomplete-list"
        role="listbox"
        style={{ top: mentionPos.top, left: mentionPos.left }}
        // Mantener el foco del editor al interactuar con el dropdown.
        onMouseDown={(e) => {
          mentionInteractingRef.current = true;
          e.preventDefault();
        }}
        onMouseUp={() => { mentionInteractingRef.current = false; }}
      >
        <div className="mention-ac-header">
          <span>Mencionar</span>
          <span className="mention-ac-hint">
            <kbd>↑</kbd><kbd>↓</kbd> navegar · <kbd>↵</kbd> elegir
          </span>
        </div>

        {mentionLoading && items.length === 0 ? (
          <div className="mention-ac-state">
            <span className="mention-ac-spinner" aria-hidden="true" />
            Cargando usuarios…
          </div>
        ) : items.length === 0 ? (
          <div className="mention-ac-state">
            {mentionQuery
              ? `Sin coincidencias para “${mentionQuery}”`
              : 'No hay usuarios para mencionar'}
          </div>
        ) : (
          items.map((user, i) => (
            <button
              key={user.id}
              type="button"
              role="option"
              aria-selected={i === mentionIndex}
              className={`mention-ac-item ${i === mentionIndex ? 'is-active' : ''}`}
              onMouseEnter={() => setMentionIndex(i)}
              onClick={() => insertMention(user)}
            >
              <span className="mention-ac-avatar">
                {user.avatar
                  ? <img src={user.avatar} alt="" />
                  : mentionInitials(user.nombre)}
              </span>
              <span className="mention-ac-name">
                {renderHighlightedName(user.nombre)}
              </span>
            </button>
          ))
        )}
      </div>
    );
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleContentChange();
  };

  const handleSelection = () => {
    const selection = window.getSelection();
    setSelectedText(selection.toString());
    // Si el caret se movió (clic, flechas) reevaluar/cancelar la mención.
    if (enableMentions && !readonly) {
      detectMentionTrigger();
    }
  };

  const insertLink = () => {
    const url = prompt('Introduce la URL:');
    if (url) {
      execCommand('createLink', url);
    }
  };

  const insertHorizontalRule = () => {
    execCommand('insertHorizontalRule');
  };

  const toggleCodeBlock = () => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    // Buscar si ya estamos dentro de un <pre>
    let node = selection.anchorNode;
    let preParent = null;
    while (node && node !== editorRef.current) {
      if (node.nodeType === 1 && node.tagName === 'PRE') {
        preParent = node;
        break;
      }
      node = node.parentNode;
    }

    if (preParent) {
      // Ya estamos en un <pre> → deshacer: reemplazar el <pre> por un <p>
      const p = document.createElement('p');
      // textContent como texto: si el bloque contenía "<img onerror=...>"
      // escrito como código no debe volver a interpretarse como HTML.
      if (preParent.textContent) p.textContent = preParent.textContent;
      else p.innerHTML = '<br>';
      preParent.parentNode.replaceChild(p, preParent);

      // Posicionar cursor dentro del nuevo <p>
      const range = document.createRange();
      range.selectNodeContents(p);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      // No estamos en un <pre> → aplicar formatBlock
      document.execCommand('formatBlock', false, 'pre');
    }

    editorRef.current?.focus();
    handleContentChange();
  };

  const toggleBlockquote = () => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    let node = selection.anchorNode;
    let bqParent = null;
    while (node && node !== editorRef.current) {
      if (node.nodeType === 1 && node.tagName === 'BLOCKQUOTE') {
        bqParent = node;
        break;
      }
      node = node.parentNode;
    }

    if (bqParent) {
      const p = document.createElement('p');
      p.innerHTML = bqParent.innerHTML || '<br>';
      bqParent.parentNode.replaceChild(p, bqParent);

      const range = document.createRange();
      range.selectNodeContents(p);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      document.execCommand('formatBlock', false, 'blockquote');
    }

    editorRef.current?.focus();
    handleContentChange();
  };

  const applyTextColor = (color) => {
    execCommand('foreColor', color);
  };

  const applyBackgroundColor = (color) => {
    execCommand('hiliteColor', color);
  };

  const handleHTMLChange = (e) => {
    setHtmlCode(e.target.value);
  };

  const openTableModal = () => {
    setShowTableModal(true);
  };

  const insertTable = () => {
    let tableHTML = '<table border="1" style="border-collapse: collapse; width: 100%; margin: 10px 0;">';
    
    for (let i = 0; i < tableRows; i++) {
      tableHTML += '<tr>';
      for (let j = 0; j < tableCols; j++) {
        tableHTML += '<td style="border: 1px solid #ddd; padding: 8px; min-width: 50px;">&nbsp;</td>';
      }
      tableHTML += '</tr>';
    }
    
    tableHTML += '</table><p><br></p>';
    
    document.execCommand('insertHTML', false, tableHTML);
    handleContentChange();
    setShowTableModal(false);
  };

  const selectTable = (table, event) => {
    const previousSelected = editorRef.current?.querySelector('.rte-table-selected');
    if (previousSelected) {
      previousSelected.classList.remove('rte-table-selected');
    }
    
    setSelectedTable(table);
    table.classList.add('rte-table-selected');
    
    const rect = table.getBoundingClientRect();
    const editorRect = editorRef.current.getBoundingClientRect();
    
    setTableMenuPosition({
      top: rect.top - editorRect.top - 40,
      left: rect.left - editorRect.left
    });
    
    setShowTableMenu(false);
  };

  const addRowAbove = () => {
    if (!selectedTable) return;
    
    const rows = selectedTable.rows;
    if (rows.length === 0) return;
    
    const firstRow = rows[0];
    const newRow = selectedTable.insertRow(0);
    
    for (let i = 0; i < firstRow.cells.length; i++) {
      const newCell = newRow.insertCell();
      newCell.style.border = '1px solid #ddd';
      newCell.style.padding = '8px';
      newCell.innerHTML = '&nbsp;';
    }
    
    setShowTableMenu(false);
    handleContentChange();
  };

  const addRowBelow = () => {
    if (!selectedTable) return;
    
    const rows = selectedTable.rows;
    if (rows.length === 0) return;
    
    const lastRow = rows[rows.length - 1];
    const newRow = selectedTable.insertRow();
    
    for (let i = 0; i < lastRow.cells.length; i++) {
      const newCell = newRow.insertCell();
      newCell.style.border = '1px solid #ddd';
      newCell.style.padding = '8px';
      newCell.innerHTML = '&nbsp;';
    }
    
    setShowTableMenu(false);
    handleContentChange();
  };

  const addColumnLeft = () => {
    if (!selectedTable) return;
    
    const rows = selectedTable.rows;
    
    for (let i = 0; i < rows.length; i++) {
      const newCell = rows[i].insertCell(0);
      newCell.style.border = '1px solid #ddd';
      newCell.style.padding = '8px';
      newCell.innerHTML = '&nbsp;';
    }
    
    setShowTableMenu(false);
    handleContentChange();
  };

  const addColumnRight = () => {
    if (!selectedTable) return;
    
    const rows = selectedTable.rows;
    
    for (let i = 0; i < rows.length; i++) {
      const newCell = rows[i].insertCell();
      newCell.style.border = '1px solid #ddd';
      newCell.style.padding = '8px';
      newCell.innerHTML = '&nbsp;';
    }
    
    setShowTableMenu(false);
    handleContentChange();
  };

  const deleteRow = () => {
    if (!selectedTable) return;
    
    const rows = selectedTable.rows;
    if (rows.length <= 1) {
      showWarning('No puedes eliminar la única fila. Elimina la tabla completa si quieres.');
      return;
    }
    
    selectedTable.deleteRow(rows.length - 1);
    setShowTableMenu(false);
    handleContentChange();
  };

  const deleteColumn = () => {
    if (!selectedTable) return;
    
    const rows = selectedTable.rows;
    if (rows.length === 0) return;
    
    const colCount = rows[0].cells.length;
    if (colCount <= 1) {
      showWarning('No puedes eliminar la única columna. Elimina la tabla completa si quieres.');
      return;
    }
    
    for (let i = 0; i < rows.length; i++) {
      rows[i].deleteCell(rows[i].cells.length - 1);
    }
    
    setShowTableMenu(false);
    handleContentChange();
  };

  const deleteTable = async () => {
    if (!selectedTable) return;

    const ok = await confirm('¿Estás seguro de que quieres eliminar esta tabla?', {
      title: 'Eliminar tabla',
      confirmText: 'Eliminar',
      cancelText: 'Cancelar'
    });
    if (ok) {
      selectedTable.remove();
      setSelectedTable(null);
      setTableMenuPosition(null);
      setShowTableMenu(false);
      handleContentChange();
    }
  };

  const uploadImageToServer = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    // Subida multipart: el apiClient NO fija Content-Type (boundary automático),
    // pone el Bearer y deriva la base por tenant.
    const result = await apiClient.upload(`upload&task_id=${taskId}`, formData, { token });

    if (result.status !== 'success') {
      throw new Error(result.message || 'Error al subir archivo');
    }

    return result.data;
  };

  const handleImageUploadToServer = async (file) => {
    if (!file.type.startsWith('image/')) {
      showWarning('El archivo debe ser una imagen válida');
      return;
    }

    const maxSize = 25 * 1024 * 1024;
    if (file.size > maxSize) {
      showWarning('La imagen es demasiado grande. Máximo 25MB');
      return;
    }

    try {
      setIsUploadingImage(true);
      const uploadedFile = await uploadImageToServer(file);

      const imageUrl = uploadedFile.url;
      const imgHtml = `<img src="${imageUrl}" alt="${uploadedFile.originalName || 'Imagen pegada'}" style="max-width: 100%; height: auto;" data-attachment-id="${uploadedFile.id}" />`;

      document.execCommand('insertHTML', false, imgHtml);
      handleContentChange();

    } catch (error) {
      console.error('Error uploading image:', error);
      showError('Error al subir imagen: ' + error.message);
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleImageUploadBase64 = async (file) => {
    try {
      setIsUploadingImage(true);
      
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const imgHtml = `<img src="${e.target.result}" alt="Imagen pegada" style="max-width: 100%; height: auto;" />`;
        document.execCommand('insertHTML', false, imgHtml);
        handleContentChange();
        setIsUploadingImage(false);
      };
      
      reader.onerror = () => {
        showError('Error al procesar la imagen');
        setIsUploadingImage(false);
      };

      reader.readAsDataURL(file);

    } catch (error) {
      showError('Error al procesar imagen: ' + error.message);
      setIsUploadingImage(false);
    }
  };

  const insertImage = () => {
    if (!taskId) {
      fileInputRef.current?.click();
      return;
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    
    input.onchange = async (event) => {
      const file = event.target.files[0];
      if (!file) return;
      await handleImageUploadToServer(file);
    };

    input.click();
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        execCommand('insertImage', e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const applyHeading = (level) => {
    execCommand('formatBlock', `h${level}`);
  };

  const getPlainText = () => htmlToText(content);

  const isCommandActive = (command) => {
    try {
      return document.queryCommandState(command);
    } catch (e) {
      return false;
    }
  };

  const toolbarButtons = [
    {
      group: 'format',
      items: [
        {
          type: 'select',
          label: 'Formato',
          options: [
            { value: 'div', label: 'Párrafo' },
            { value: 'h1', label: 'Título 1' },
            { value: 'h2', label: 'Título 2' },
            { value: 'h3', label: 'Título 3' },
            { value: 'h4', label: 'Título 4' },
          ],
          onChange: (value) => applyHeading(value.replace('h', ''))
        }
      ]
    },
    {
      group: 'style',
      items: [
        {
          icon: Bold,
          command: 'bold',
          title: 'Negrita (Ctrl+B)',
          active: () => isCommandActive('bold')
        },
        {
          icon: Italic,
          command: 'italic',
          title: 'Cursiva (Ctrl+I)',
          active: () => isCommandActive('italic')
        },
        {
          icon: Underline,
          command: 'underline',
          title: 'Subrayado (Ctrl+U)',
          active: () => isCommandActive('underline')
        },
        {
          icon: Strikethrough,
          command: 'strikeThrough',
          title: 'Tachado',
          active: () => isCommandActive('strikeThrough')
        },
        {
          icon: Eraser,
          action: clearFormatting,
          title: 'Limpiar formato del texto seleccionado',
          disabled: () => !selectedText
        }
      ]
    },
    {
      group: 'colors',
      items: [
        {
          type: 'color',
          icon: Type,
          title: 'Color de texto',
          action: applyTextColor,
          colorType: 'text'
        },
        {
          type: 'color',
          icon: Highlighter,
          title: 'Color de fondo/resaltado',
          action: applyBackgroundColor,
          colorType: 'background'
        }
      ]
    },
    {
      group: 'lists',
      items: [
        {
          icon: List,
          command: 'insertUnorderedList',
          title: 'Lista con viñetas',
          active: () => isCommandActive('insertUnorderedList')
        },
        {
          icon: ListOrdered,
          command: 'insertOrderedList',
          title: 'Lista numerada',
          active: () => isCommandActive('insertOrderedList')
        }
      ]
    },
    {
      group: 'align',
      items: [
        {
          icon: AlignLeft,
          command: 'justifyLeft',
          title: 'Alinear a la izquierda',
          active: () => isCommandActive('justifyLeft')
        },
        {
          icon: AlignCenter,
          command: 'justifyCenter',
          title: 'Centrar',
          active: () => isCommandActive('justifyCenter')
        },
        {
          icon: AlignRight,
          command: 'justifyRight',
          title: 'Alinear a la derecha',
          active: () => isCommandActive('justifyRight')
        }
      ]
    },
    {
      group: 'insert',
      items: [
        {
          icon: Link,
          action: insertLink,
          title: 'Insertar enlace',
          disabled: () => !selectedText
        },
        {
          icon: Image,
          action: insertImage,
          title: taskId ? 'Insertar imagen (se sube al servidor)' : 'Insertar imagen (base64)',
          loading: isUploadingImage
        },
        {
          icon: Quote,
          action: toggleBlockquote,
          title: 'Cita (Doble Enter para salir)'
        },
        {
          icon: Code,
          action: toggleCodeBlock,
          title: 'Código (Doble Enter para salir)'
        },
        {
          icon: Minus,
          action: insertHorizontalRule,
          title: 'Insertar línea horizontal'
        },
        {
          icon: Table,
          action: openTableModal,
          title: 'Insertar tabla'
        }
      ]
    },
    {
      group: 'image',
      items: [
        {
          icon: Settings,
          action: openResizeModal,
          title: 'Ajustar tamaño de imagen seleccionada',
          disabled: () => !selectedImage
        }
      ]
    },
    {
      group: 'history',
      items: [
        {
          icon: Undo,
          command: 'undo',
          title: 'Deshacer (Ctrl+Z)'
        },
        {
          icon: Redo,
          command: 'redo',
          title: 'Rehacer (Ctrl+Y)'
        }
      ]
    }
  ];

  useEffect(() => {
    if (isPreviewMode || readonly) {
      deselectImage();
    }
  }, [isPreviewMode, readonly]);

  useEffect(() => {
    return () => {
      const allImages = document.querySelectorAll('.rte-image-selected');
      allImages.forEach(img => {
        img.classList.remove('rte-image-selected');
      });
    };
  }, []);

  const ResizeModal = () => {
    if (!showResizeModal || !selectedImage) return null;

    const imgId = selectedImage.src + selectedImage.alt;
    const originalDims = originalImageDimensions[imgId];
    const currentRect = selectedImage.getBoundingClientRect();

    return (
      <div className="rte-modal-overlay" onClick={() => setShowResizeModal(false)}>
        <div className="rte-modal" onClick={(e) => e.stopPropagation()}>
          <div className="rte-modal-header">
            <h3>Ajustar tamaño de imagen</h3>
            <button 
              className="rte-modal-close"
              onClick={() => setShowResizeModal(false)}
            >
              <X size={20} />
            </button>
          </div>
          
          <div className="rte-modal-body">
            <div className="rte-image-preview">
              <img 
                src={selectedImage.src} 
                alt="Vista previa" 
                style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'contain' }}
              />
            </div>
            
            <div className="rte-image-info">
              <p><strong>Tamaño actual:</strong> {Math.round(currentRect.width)} x {Math.round(currentRect.height)} px</p>
              {originalDims && (
                <p><strong>Tamaño original:</strong> {originalDims.width} x {originalDims.height} px</p>
              )}
            </div>

            <div className="rte-size-options">
              <h4>Selecciona un tamaño:</h4>
              <div className="rte-size-grid">
                {imageSizes.map((size, index) => {
                  const isOriginal = size.width === 'original';
                  const isDisabled = isOriginal && !originalDims;
                  
                  return (
                    <button
                      key={index}
                      className="rte-size-button"
                      disabled={isDisabled}
                      onClick={() => applyImageSize(size)}
                      title={isDisabled ? 'Dimensiones originales no disponibles' : `Cambiar a ${size.name}`}
                    >
                      <div className="rte-size-name">{size.name}</div>
                      <div className="rte-size-label">{size.label}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const TableModal = () => {
    if (!showTableModal) return null;

    return (
      <div className="rte-modal-overlay" onClick={() => setShowTableModal(false)}>
        <div className="rte-modal rte-table-modal" onClick={(e) => e.stopPropagation()}>
          <div className="rte-modal-header">
            <h3>Insertar tabla</h3>
            <button 
              className="rte-modal-close"
              onClick={() => setShowTableModal(false)}
            >
              <X size={20} />
            </button>
          </div>
          
          <div className="rte-modal-body">
            <div className="rte-table-config">
              <div className="rte-table-input-group">
                <label>Filas:</label>
                <input 
                  type="number" 
                  min="1" 
                  max="20" 
                  value={tableRows}
                  onChange={(e) => setTableRows(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
                />
              </div>
              
              <div className="rte-table-input-group">
                <label>Columnas:</label>
                <input 
                  type="number" 
                  min="1" 
                  max="10" 
                  value={tableCols}
                  onChange={(e) => setTableCols(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                />
              </div>
            </div>

            <div className="rte-table-preview">
              <p>Vista previa: Tabla de {tableRows}x{tableCols}</p>
            </div>

            <div className="rte-modal-actions">
              <button 
                className="rte-button rte-button-secondary"
                onClick={() => setShowTableModal(false)}
              >
                Cancelar
              </button>
              <button 
                className="rte-button rte-button-primary"
                onClick={insertTable}
              >
                Insertar tabla
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const TableContextMenu = () => {
    if (!selectedTable || !tableMenuPosition) return null;

    return (
      <>
        <button
          className="rte-table-menu-button"
          onClick={() => setShowTableMenu(!showTableMenu)}
          style={{
            position: 'absolute',
            top: `${tableMenuPosition.top}px`,
            left: `${tableMenuPosition.left}px`,
            zIndex: 1001
          }}
          title="Opciones de tabla"
        >
          <MoreVertical size={16} />
        </button>

        {showTableMenu && (
          <div 
            className="rte-table-menu"
            style={{
              position: 'absolute',
              top: `${tableMenuPosition.top + 35}px`,
              left: `${tableMenuPosition.left}px`,
              zIndex: 1000
            }}
          >
            <button onClick={addRowAbove} title="Añadir fila arriba">
              <ArrowUp size={14} /> Fila arriba
            </button>
            <button onClick={addRowBelow} title="Añadir fila abajo">
              <ArrowDown size={14} /> Fila abajo
            </button>
            <button onClick={addColumnLeft} title="Añadir columna izquierda">
              <ArrowLeft size={14} /> Columna izq.
            </button>
            <button onClick={addColumnRight} title="Añadir columna derecha">
              <ArrowRight size={14} /> Columna der.
            </button>
            <button onClick={deleteRow} title="Eliminar fila">
              <Minus size={14} /> Eliminar fila
            </button>
            <button onClick={deleteColumn} title="Eliminar columna">
              <Minus size={14} /> Eliminar col.
            </button>
            <button onClick={deleteTable} title="Eliminar tabla completa" style={{ color: '#dc2626' }}>
              <Trash2 size={14} /> Eliminar tabla
            </button>
          </div>
        )}
      </>
    );
  };

  editorHandlersRef.current = { handleContentChange, handleImageUploadToServer, handleImageUploadBase64 };

  if (readonly) {
    return (
      <div className={`rich-text-editor readonly ${className}`}>
        <div
          ref={(el) => { if (el) requestAnimationFrame(() => highlightCodeBlocks(el).catch(() => {})); }}
          className="rich-text-preview"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }}
          style={{ minHeight: height }}
        />
      </div>
    );
  }

  return (
    <div ref={editorContainerRef} className={`rich-text-editor ${className}`}>
      {showToolbar && toolbarStuck && (
        <div style={{ height: toolbarRef.current?.offsetHeight || 52 }} aria-hidden="true" />
      )}
      {showToolbar && (
        <div
          ref={toolbarRef}
          className={`rich-text-toolbar ${toolbarStuck ? 'rich-text-toolbar-stuck' : ''} ${toolbarOpen ? 'format-open' : 'format-collapsed'}`}
          style={toolbarStuck ? {
            position: 'fixed',
            top: toolbarDimensions.top || 0,
            left: toolbarDimensions.left,
            width: toolbarDimensions.width,
            zIndex: 1000,
          } : undefined}>
          {/* Toggle solo-móvil: despliega/colapsa los grupos de formato */}
          <button
            type="button"
            className="toolbar-format-toggle"
            onClick={() => setToolbarOpen(v => !v)}
            title={toolbarOpen ? 'Ocultar formato' : 'Mostrar formato'}
            aria-expanded={toolbarOpen}
          >
            <Type size={16} />
            <span>Formato</span>
          </button>
          {toolbarButtons.map((group, groupIndex) => (
            <div key={groupIndex} className="toolbar-group">
              {group.items.map((item, itemIndex) => {
                if (item.type === 'select') {
                  return (
                    <select
                      key={itemIndex}
                      className="toolbar-select"
                      onChange={(e) => item.onChange(e.target.value)}
                      title={item.title}
                    >
                      {item.options.map((option, optionIndex) => (
                        <option key={optionIndex} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  );
                }

                if (item.type === 'color') {
                  const Icon = item.icon;
                  return (
                    <div key={itemIndex} className="toolbar-color-picker">
                      <input
                        type="color"
                        id={`color-${item.colorType}-${itemIndex}`}
                        className="toolbar-color-input"
                        onChange={(e) => {
                          item.action(e.target.value);
                          editorRef.current?.focus();
                        }}
                        defaultValue={item.colorType === 'text' ? '#000000' : '#ffff00'}
                        title={item.title}
                      />
                      <label 
                        htmlFor={`color-${item.colorType}-${itemIndex}`}
                        className="toolbar-button toolbar-color-label"
                        title={item.title}
                      >
                        <Icon size={16} />
                      </label>
                    </div>
                  );
                }

                const Icon = item.icon;
                const isActive = item.active ? item.active() : false;
                const isDisabled = item.disabled ? item.disabled() : false;
                const isLoading = item.loading || false;

                return (
                  <button
                    key={itemIndex}
                    type="button"
                    className={`toolbar-button ${isActive ? 'active' : ''} ${isDisabled ? 'disabled' : ''}`}
                    onClick={() => {
                      if (item.action) {
                        item.action();
                      } else if (item.command) {
                        execCommand(item.command, item.value);
                      }
                    }}
                    disabled={isDisabled || isLoading}
                    title={item.title}
                  >
                    {isLoading ? <Loader2 size={16} className="spinning" /> : <Icon size={16} />}
                  </button>
                );
              })}
            </div>
          ))}

          <div className="toolbar-group mode-buttons">
            <button
              type="button"
              className={`mode-button ${!isPreviewMode && !showHTMLMode ? 'active' : ''}`}
              onClick={() => {
                if (showHTMLMode) {
                  setContent(htmlCode);
                  if (onChange) {
                    onChange(htmlCode);
                  }
                  setTimeout(() => {
                    if (editorRef.current) {
                      editorRef.current.innerHTML = sanitizeHtml(htmlCode);
                    }
                  }, 0);
                }
                setIsPreviewMode(false);
                setShowHTMLMode(false);
              }}
              title="Modo edición visual"
            >
              <Edit3 size={14} />
              Editar
            </button>
            <button
              type="button"
              className={`mode-button ${showHTMLMode ? 'active' : ''}`}
              onClick={() => {
                if (!showHTMLMode) {
                  const currentContent = editorRef.current ? editorRef.current.innerHTML : content;
                  setHtmlCode(currentContent);
                  setContent(currentContent);
                }
                setShowHTMLMode(!showHTMLMode);
                setIsPreviewMode(false);
              }}
              title="Ver/Editar código HTML"
            >
              <FileCode size={14} />
              HTML
            </button>
            <button
              type="button"
              className={`mode-button ${isPreviewMode ? 'active' : ''}`}
              onClick={() => {
                if (showHTMLMode) {
                  setContent(htmlCode);
                  if (onChange) {
                    onChange(htmlCode);
                  }
                }
                setIsPreviewMode(true);
                setShowHTMLMode(false);
              }}
              title="Vista previa"
            >
              <Eye size={14} />
              Vista previa
            </button>
          </div>
        </div>
      )}

      <div className="rich-text-content" style={{ minHeight: height }}>
        {showHTMLMode ? (
          <textarea
            className="rich-text-html-editor"
            value={htmlCode}
            onChange={handleHTMLChange}
            spellCheck={false}
            style={{ 
              minHeight: height,
              width: '100%',
              padding: '12px',
              fontFamily: 'monospace',
              fontSize: '13px',
              border: '1px solid #e5e7eb',
              borderRadius: '4px',
              resize: 'vertical'
            }}
          />
        ) : isPreviewMode ? (
          <div
            ref={previewRef}
            className="rich-text-preview"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }}
          />
        ) : (
          <div style={{ position: 'relative' }}>
            <div
              ref={editorRef}
              className="rich-text-content"
              contentEditable={!readonly}
              onInput={handleContentChange}
              onMouseUp={handleSelection}
              onKeyUp={handleSelection}
              onKeyDown={handleKeyDown}
              onBlur={enableMentions ? handleEditorBlur : undefined}
              data-placeholder={placeholder}
              suppressContentEditableWarning={true}
              style={{ minHeight: height }}
              role={enableMentions ? 'textbox' : undefined}
              aria-multiline={enableMentions ? 'true' : undefined}
              aria-autocomplete={enableMentions ? 'list' : undefined}
              aria-expanded={enableMentions ? mentionOpen : undefined}
              aria-controls={enableMentions && mentionOpen ? 'mention-autocomplete-list' : undefined}
            />
            <TableContextMenu />
            <MentionAutocomplete />
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="rich-text-file-input"
        onChange={handleImageUpload}
        style={{ display: 'none' }}
      />

      {!readonly && (
        <div className="rich-text-info">
          <div className="rich-text-counter">
            {getPlainText().length} caracteres
            {selectedText && ` · ${selectedText.length} seleccionados`}
            {isUploadingImage && ' · Subiendo imagen...'}
            {selectedImage && ' · Imagen seleccionada'}
            {selectedTable && ' · Tabla seleccionada'}
            {showHTMLMode && ' · Modo HTML - Edita el código directamente'}
          </div>
          
          <div className="rich-text-shortcuts">
            Ctrl+B negrita · Ctrl+I cursiva · Ctrl+U subrayado
            {taskId && ' · Pega imágenes (Ctrl+V)'}
            {' · Doble Enter para salir de Quote/Code'}
            {!showHTMLMode && ' · Click en imagen para seleccionar'}
            {!showHTMLMode && ' · TAB para navegar en tablas'}
            {showHTMLMode && ' · Click "Editar" para aplicar cambios'}
          </div>
        </div>
      )}

      <ResizeModal />
      <TableModal />

      <style>{`
        .spinning {
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .rich-text-content .rte-image-selected {
          outline: 2px solid #3b82f6;
          outline-offset: 2px;
        }

        .rich-text-file-input {
          display: none;
        }

        .toolbar-color-picker {
          position: relative;
          display: inline-block;
        }

        .toolbar-color-input {
          position: absolute;
          left: 0;
          top: 0;
          opacity: 0;
          width: 32px;
          height: 32px;
          cursor: pointer;
          z-index: 2;
        }

        .toolbar-color-label {
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          position: relative;
          z-index: 1;
          pointer-events: none;
        }

        .toolbar-color-picker:hover .toolbar-color-label {
          background-color: rgba(0, 0, 0, 0.05);
        }

        .rich-text-content .rte-table-selected {
          outline: 2px solid #3b82f6;
          outline-offset: 2px;
        }

        .rte-table-modal {
          min-width: 400px;
        }

        .rte-table-config {
          display: flex;
          gap: 20px;
          margin-bottom: 20px;
        }

        .rte-table-input-group {
          flex: 1;
        }

        .rte-table-input-group label {
          display: block;
          margin-bottom: 8px;
          font-weight: 500;
          color: #374151;
        }

        .rte-table-input-group input {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          font-size: 14px;
        }

        .rte-table-preview {
          padding: 16px;
          background-color: #f9fafb;
          border-radius: 4px;
          margin-bottom: 20px;
          text-align: center;
          color: #6b7280;
        }

        .rte-modal-actions {
          display: flex;
          gap: 10px;
          justify-content: flex-end;
        }

        .rte-button {
          padding: 8px 16px;
          border: none;
          border-radius: 4px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .rte-button-primary {
          background-color: #3b82f6;
          color: white;
        }

        .rte-button-primary:hover {
          background-color: #2563eb;
        }

        .rte-button-secondary {
          background-color: #e5e7eb;
          color: #374151;
        }

        .rte-button-secondary:hover {
          background-color: #d1d5db;
        }

        .rte-table-menu {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          padding: 4px;
          display: flex;
          flex-direction: column;
          gap: 2px;
          min-width: 180px;
        }

        .rte-table-menu button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          border: none;
          background: transparent;
          color: #374151;
          font-size: 14px;
          cursor: pointer;
          border-radius: 4px;
          text-align: left;
          transition: background-color 0.15s;
        }

        .rte-table-menu button:hover {
          background-color: #f3f4f6;
        }

        .rte-table-menu button svg {
          flex-shrink: 0;
        }

        .rte-table-menu-button {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 4px;
          padding: 6px;
          cursor: pointer;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .rte-table-menu-button:hover {
          background-color: #f3f4f6;
          border-color: #3b82f6;
        }

        .rte-table-menu-button:active {
          background-color: #e5e7eb;
        }
      `}</style>
    </div>
  );
}

export function RichTextViewer({ content, className = '' }) {
  return (
    <div className={`rich-text-editor readonly ${className}`}>
      <div
        ref={(el) => { if (el) requestAnimationFrame(() => highlightCodeBlocks(el).catch(() => {})); }}
        className="rich-text-preview"
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }}
      />
    </div>
  );
}

export function useRichTextPlain(htmlContent) {
  return htmlToText(htmlContent);
}

export default RichTextEditor;